import React, {useContext, useEffect, useState} from 'react';
import {AppContext} from '../context/AppContext';
import { WorklogInput } from '../types/api';
import {DayGroup} from '../services/WorklogStore';
import {Worklog} from "../services/Worklog";
import IssueAutocompleteInput from "./IssueAutocompleteInput";
import DescriptionAutocompleteInput from "./DescriptionAutocompleteInput";
import {useIssueValidation} from '../hooks/useIssueValidation';
import {DescriptionHistoryService} from '../services/DescriptionHistoryService';
import ConfirmDialog from './ConfirmDialog';

const WorklogList: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('Worklog must be used within AppContextProvider');
  }

  const {i18n, store, jiraClient, searchIssues, timer, startTimer, stopTimer, autoSync, syncQueue} = context;

  // Helper to format Date for datetime-local input (preserves local timezone)
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    issueKey: string,
    description: string,
    start: string,
    end: string
  }>({issueKey: '', description: '', start: '', end: ''});
  const [saving, setSaving] = useState(false);
  const [groupedData, setGroupedData] = useState<DayGroup[]>(() => store.groupByDay(i18n));
  const [worklogToDelete, setWorklogToDelete] = useState<Worklog | null>(null);

  // Centralized validation for issue key
  const { isIssueValid, validatingIssue } = useIssueValidation(editForm.issueKey, searchIssues);

  // Make the grouped data reactive to store changes
  useEffect(() => {
    const updateGroupedData = () => {
      // Reload from storage to reflect changes made by SyncQueue or other tabs
      if (store && typeof store.reload === 'function') {
        store.reload();
      }
      setGroupedData(store.groupByDay(i18n));
    };

    // Update immediately
    updateGroupedData();

    // Set up interval to check for changes (simple polling approach)
    const interval = setInterval(updateGroupedData, 1000);

    return () => clearInterval(interval);
  }, [store, i18n]);

  const handleEdit = (worklog: any) => {
    if (worklog.pendingDelete) {
      return;
    }
    setEditingId(worklog.id);
    setEditForm({
      issueKey: worklog.issueKey || '',
      description: worklog.description || '',
      start: worklog.start ? formatDateTimeLocal(worklog.start) : '',
      end: worklog.end ? formatDateTimeLocal(worklog.end) : ''
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({issueKey: '', description: '', start: '', end: ''});
  };

  const handleRestart = (wl: Worklog) => {
    // Stop current timer if running and persist worklog
    if (timer.isRunning()) {
      const stopped = stopTimer();
      if (stopped) {
        // Add stopped worklog description to history
        if (stopped.description) {
          DescriptionHistoryService.addDescription(stopped.description);
        }
        store.add(new Worklog(stopped));
      }
    }
    const key = wl.issueKey;
    const desc = wl.description || '';
    startTimer(key, desc);
  };

  const canSave = isIssueValid === true;

  const handleSave = async (worklog: Worklog) => {
    if (!canSave) {
      alert(i18n.t('invalidOrUnknownIssue'));
      return;
    }

    setSaving(true);
    try {
      const startDate = editForm.start ? new Date(editForm.start) : worklog.start;
      const endDate = editForm.end ? new Date(editForm.end) : worklog.end || null;
      const durationSec = (startDate && endDate)
        ? Math.floor((endDate.getTime() - startDate.getTime()) / 1000)
        : worklog.durationSec;

      const description = (editForm.description || '').trim() || worklog.description;

      // Add description to history
      if (description) {
        DescriptionHistoryService.addDescription(description);
      }

      const worklogData: WorklogInput = {
        issueKey: editForm.issueKey.trim(),
        description: description,
        start: startDate,
        durationSec: durationSec
      };

      const oldIssueKey = worklog.issueKey;
      const hasJiraId = !!worklog.jiraId;
      const issueChanged = hasJiraId && oldIssueKey && oldIssueKey !== worklogData.issueKey;

      if (issueChanged) {
        // Move worklog: create on new issue, then delete from old issue (best-effort)
        const createRes = await jiraClient.upsertWorklog(worklogData);
        const createdJiraId = createRes && createRes.id ? createRes.id : null;

        // Best-effort cleanup of old worklog; don't fail the whole operation if deletion fails
        try {
          if (worklog.jiraId) {
            await jiraClient.deleteWorklog(oldIssueKey, worklog.jiraId);
          }
        } catch (delErr) {
          console.warn('Failed to delete old worklog during move:', delErr);
        }

        // Update store with new Jira linkage and new issue key
        store.update({
          ...worklog,
          ...worklogData,
          jiraId: createdJiraId,
          start: createRes.start,
          end: createRes.end,
          durationSec: createRes.durationSec,
          status: 'synced',
          syncedAt: new Date(),
          updatedAt: new Date()
        } as Worklog);
      } else {
        // Same issue: normal upsert
        const res = await jiraClient.upsertWorklog({
          ...worklogData,
          jiraId: worklog.jiraId || undefined
        });

        const newJiraId = res && res.id ? res.id : (worklog.jiraId || null);

        store.update({
          ...worklog,
          ...worklogData,
          jiraId: newJiraId,
          start: res.start,
          end: res.end,
          durationSec: res.durationSec,
          status: 'synced',
          syncedAt: new Date(),
          updatedAt: new Date()
        } as Worklog);
      }

      setEditingId(null);
      setEditForm({issueKey: '', description: '', start: '', end: ''});
    } catch (error) {
      console.error('Failed to upload worklog:', error);
      alert(i18n.t('Failed to save worklog: ') + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="worklog-list" className="worklog-list">
      {groupedData.map(group => (
        <div key={group.key} className="group card">
          <h3>
            {group.label} -&nbsp;
            <span>
            {(() => {
              const hours = Math.floor(group.totalSec / 3600);
              const minutes = Math.floor((group.totalSec % 3600) / 60);
              return `${hours}h ${minutes}m`;
            })()}
          </span>
          </h3>
          <div className="worklog-items">
            {group.items.map(worklog => (
              <div key={worklog.id} className={`worklog${worklog.pendingDelete ? ' pending-delete' : ''}`} style={worklog.pendingDelete ? {opacity: 0.5} : undefined}>
                <div className={`worklog-status ${worklog.status}`}></div>
                <div>
                  {editingId === worklog.id ? (
                    <form className="worklog-edit-form">
                      <div className="row">
                        <div className="field">
                          <label>
                            <span>{i18n.t('issueKey')}</span>
                            <IssueAutocompleteInput
                              inputId={`edit-issue-${worklog.id}`}
                              value={editForm.issueKey}
                              onChange={(v) => setEditForm({...editForm, issueKey: v})}
                              placeholder="PROJ-123"
                              required
                              pattern="[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*"
                              autoComplete="off"
                            />
                          </label>
                          {!validatingIssue && editForm.issueKey.trim() && !isIssueValid && (
                            <div className="error">
                              {i18n.t('invalidOrUnknownIssue')}
                            </div>
                          )}
                        </div>
                        <div className="field">
                          <label>
                            <span>{i18n.t('description')}</span>
                            <DescriptionAutocompleteInput
                              inputId={`edit-description-${worklog.id}`}
                              value={editForm.description}
                              onChange={(v) => setEditForm({...editForm, description: v})}
                              placeholder={i18n.t('description')}
                            />
                          </label>
                          <div className="error"></div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="field">
                          <label>
                            <span>{i18n.t('startTime')}</span>
                            <input
                              type="datetime-local"
                              value={editForm.start}
                              onChange={(e) => setEditForm({...editForm, start: e.target.value})}
                            />
                          </label>
                          <div className="error"></div>
                        </div>
                        <div className="field">
                          <label>
                            <span>{i18n.t('endTime')}</span>
                            <input
                              type="datetime-local"
                              value={editForm.end}
                              onChange={(e) => setEditForm({...editForm, end: e.target.value})}
                            />
                          </label>
                          <div className="error"></div>
                        </div>
                      </div>
                      <div className="worklog-time small">
                        Duration: {(() => {
                        const hours = Math.floor(worklog.durationSec / 3600);
                        const minutes = Math.floor((worklog.durationSec % 3600) / 60);
                        return `${hours}h ${minutes}m`;
                      })()}
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="worklog-issue">{worklog.issueKey} - {worklog.description}</div>
                      <div className="worklog-time small">
                        {worklog.start
                          ? worklog.start.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                          : null} -
                        {worklog.end
                          ? worklog.end.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                          : null} -
                        {(() => {
                          const hours = Math.floor(worklog.durationSec / 3600);
                          const minutes = Math.floor((worklog.durationSec % 3600) / 60);
                          return `${hours}h ${minutes}m`;
                        })()}
                      </div>
                      {worklog.error && (
                        <div className="worklog-error small worklog-error-inline">
                          {i18n.t('Error')}: {worklog.error}
                        </div>
                      )}
                      {worklog.pendingDelete && (
                        <div className="worklog-pending-delete small worklog-pending-inline">
                          {i18n.t('pendingDeletion')}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="worklog-actions">
                  {editingId === worklog.id ? (
                    <div className="row right">
                      <button
                        className="btn"
                        title={i18n.t('cancel')}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        {i18n.t('cancel')}
                      </button>
                      <button
                        className="btn primary"
                        title={!canSave ? i18n.t('invalidOrUnknownIssue') : i18n.t('save')}
                        onClick={() => handleSave(worklog)}
                        disabled={saving || !canSave}
                      >
                        {saving ? i18n.t('saving') || 'Saving...' : i18n.t('save')}
                      </button>
                    </div>
                  ) : (
                    <div className="row right">
                      <button
                        id={`worklog-edit-${worklog.id}`}
                        className="btn"
                        title={i18n.t('edit')}
                        onClick={() => handleEdit(worklog)}
                        disabled={worklog.pendingDelete}
                      >
                        <span>{i18n.t('edit')}</span>
                      </button>
                      <button
                        id={'worklog-delete'}
                        className="btn"
                        title={i18n.t('delete')}
                        onClick={() => setWorklogToDelete(worklog)}
                        disabled={worklog.pendingDelete}
                      >
                        <span>{i18n.t('delete')}</span>
                      </button>
                      <button
                        id={`worklog-restart-${worklog.id}`}
                        className="btn success btn-icon"
                        title={i18n.t('restart')}
                        aria-label={i18n.t('restart')}
                        onClick={() => handleRestart(worklog)}
                        disabled={worklog.pendingDelete}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path
                            fill="currentColor"
                            d="M17.65 6.35A7.95 7.95 0 0012 4a8 8 0 100 16 8 8 0 007.75-6h-2.1a6 6 0 11-1.6-5.65L14 10h7V3l-2.35 2.35z"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <ConfirmDialog
        open={!!worklogToDelete}
        title={i18n.t('deleteWorklogTitle')}
        message={i18n.t('deleteWorklogMessage')}
        confirmLabel={i18n.t('delete')}
        cancelLabel={i18n.t('cancel')}
        onCancel={() => setWorklogToDelete(null)}
        onConfirm={async () => {
          const wl = worklogToDelete;
          if (!wl) return;
          try {
            if (wl.jiraId) {
              if (autoSync && navigator.onLine) {
                // AutoSync on and online: delete from Jira immediately
                await jiraClient.deleteWorklog(wl.issueKey, wl.jiraId);
                store.remove(wl.id);
              } else {
                // Offline or AutoSync disabled: mark pending deletion and let SyncQueue handle it later
                store.update({
                  ...wl,
                  pendingDelete: true,
                  updatedAt: new Date()
                } as Worklog);
                // If AutoSync is enabled, try to kick off background sync (will no-op if offline)
                if (autoSync) {
                  try {
                    await syncQueue.process();
                  } catch {}
                }
              }
            } else {
              // No Jira linkage => delete locally now
              store.remove(wl.id);
            }
          } finally {
            setWorklogToDelete(null);
          }
        }}
        confirmType="danger"
      />
    </div>
  );
};

export default WorklogList;